import React, { useMemo } from "react";
import Field from "./shared/Field";
import GalleryItemImageUploader from "./shared/GalleryItemImageUploader";
import { sectionTemplates } from "../../../../data/database";

const ensureId = () => `p_${Math.random().toString(16).slice(2)}`;

const normalizeGalleryTemplate = (tpl) => {
  const t = String(tpl || "").toLowerCase().trim();

  if (t === "1" || t === "gallery1" || t === "template1" || t.endsWith("gallery1")) return "gallery1";
  if (t === "2" || t === "gallery2" || t === "template2" || t.endsWith("gallery2")) return "gallery2";
  if (t === "3" || t === "gallery3" || t === "template3" || t.endsWith("gallery3")) return "gallery3";

  return "gallery1";
};

const isGallery1 = (t) => normalizeGalleryTemplate(t) === "gallery1";
const isGallery2 = (t) => normalizeGalleryTemplate(t) === "gallery2";
const isGallery3 = (t) => normalizeGalleryTemplate(t) === "gallery3";

const safeArr = (v) => (Array.isArray(v) ? v : []);

function Group({ title, subtitle, defaultOpen = true, children, toggleLabel }) {
  return (
    <details open={defaultOpen} className="rounded-xl border border-gray-200 bg-white">
      <summary className="cursor-pointer select-none list-none px-4 py-3 flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-gray-900">{title}</div>
          {subtitle ? <div className="text-xs text-gray-500 mt-0.5">{subtitle}</div> : null}
        </div>
        <div className="text-xs text-gray-400 mt-0.5">{toggleLabel || "Toggle"}</div>
      </summary>
      <div className="px-4 pb-4 pt-1">{children}</div>
    </details>
  );
}

function Segmented({ value, onChange, options }) {
  return (
    <div className="inline-flex p-1 rounded-xl bg-gray-100 border border-gray-200">
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              active ? "bg-white text-gray-900 shadow-sm ring-1 ring-gray-200" : "text-gray-600 hover:text-gray-900"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export default function GallerySectionEditor({
  config,
  template,
  onChange,
  uploadImage,
  t = (k, f) => f,
}) {
  const normalizedTemplate = normalizeGalleryTemplate(template);

  const tpl = sectionTemplates?.gallery?.[normalizedTemplate] || {};
  const cfg = useMemo(() => ({ ...(tpl.config || {}), ...(config || {}) }), [tpl, config]);

  const projects = useMemo(() => safeArr(cfg?.projects), [cfg]);

  const setProjects = (next) => onChange("projects", next);

  const addProject = () => {
    const next = [
      ...projects,
      {
        id: ensureId(),
        title: t("galleryEditor.defaults.newProjectTitle", "New Project"),
        category: t("galleryEditor.defaults.newProjectCategory", "General"),
        image: "",
        description: "",
        url: "",
        results: isGallery3(normalizedTemplate)
          ? [{ value: "100%", label: t("galleryEditor.defaults.resultLabel", "Result") }]
          : undefined,
      },
    ];
    setProjects(next);
  };

  const removeProject = (id) => setProjects(projects.filter((p) => p.id !== id));

  const updateProject = (id, patch) => {
    const next = projects.map((p) => (p.id === id ? { ...p, ...patch } : p));
    setProjects(next);
  };


  const modalFeatures = safeArr(cfg?.modalFeatures);
  const setModalFeatures = (next) => onChange("modalFeatures", next);

  const addModalFeature = () =>
    setModalFeatures([...modalFeatures, t("galleryEditor.defaults.newFeature", "New feature")]);

  const updateModalFeature = (idx, val) => {
    const next = modalFeatures.map((tt, i) => (i === idx ? val : tt));
    setModalFeatures(next);
  };

  const removeModalFeature = (idx) => setModalFeatures(modalFeatures.filter((_, i) => i !== idx));

  const stats = safeArr(cfg?.stats);
  const setStats = (next) => onChange("stats", next);

  const addStat = () =>
    setStats([...stats, { value: "0", label: t("galleryEditor.defaults.newStat", "New Stat") }]);

  const updateStat = (idx, patch) => {
    const next = stats.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    setStats(next);
  };

  const removeStat = (idx) => setStats(stats.filter((_, i) => i !== idx));

  const filterButtons = safeArr(cfg?.filterButtons);
  const setFilterButtons = (next) => onChange("filterButtons", next);

  const addFilter = () =>
    setFilterButtons([...filterButtons, { id: "new", label: t("galleryEditor.defaults.newFilter", "New") }]);

  const updateFilter = (idx, patch) => {
    const next = filterButtons.map((b, i) => (i === idx ? { ...b, ...patch } : b));
    setFilterButtons(next);
  };

  const removeFilter = (idx) => setFilterButtons(filterButtons.filter((_, i) => i !== idx));

  const updateResult = (projectId, idx, patch) => {
    const p = projects.find((x) => x.id === projectId);
    const arr = safeArr(p?.results);
    const nextArr = arr.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    updateProject(projectId, { results: nextArr });
  };

  const addResult = (projectId) => {
    const p = projects.find((x) => x.id === projectId);
    const arr = safeArr(p?.results);
    updateProject(projectId, {
      results: [...arr, { value: "0", label: t("galleryEditor.defaults.newResult", "New Result") }],
    });
  };

  const removeResult = (projectId, idx) => {
    const p = projects.find((x) => x.id === projectId);
    const arr = safeArr(p?.results);
    updateProject(projectId, { results: arr.filter((_, i) => i !== idx) });
  };

  return (
    <div className="space-y-4">
      <Group
        title={t("galleryEditor.group.layoutAndHeader", "Layout & Header")}
        subtitle={t("galleryEditor.group.layoutAndHeaderHelp", "Direction and main section texts")}
        toggleLabel={t("galleryEditor.group.layoutAndHeaderToggle", "Toggle")}
        defaultOpen
      >
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">
              {t("galleryEditor.layoutDirection", "Layout Direction")}
            </label>

            <div className="mt-2">
              <Segmented
                value={cfg.direction || "ltr"}
                onChange={(v) => onChange("direction", v)}
                options={[
                  { value: "ltr", label: t("galleryEditor.ltr", "LTR") },
                  { value: "rtl", label: t("galleryEditor.rtl", "RTL") },
                ]}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field
              label={t("galleryEditor.fields.title", "Title")}
              value={cfg?.title || ""}
              onChange={(v) => onChange("title", v)}
            />
            <Field
              label={t("galleryEditor.fields.subtitle", "Subtitle")}
              value={cfg?.subtitle || ""}
              onChange={(v) => onChange("subtitle", v)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field
              label={t("galleryEditor.fields.emptyStateText", "Empty State Text")}
              value={cfg?.emptyStateText || ""}
              onChange={(v) => onChange("emptyStateText", v)}
              hint={t("galleryEditor.hints.emptyStateText", "Shown when there are no items to display")}
            />

            {isGallery3(normalizedTemplate) && (
              <Field
                label={t("galleryEditor.fields.moreProjectsLabel", "More Projects Label")}
                value={cfg?.moreProjectsLabel || ""}
                onChange={(v) => onChange("moreProjectsLabel", v)}
                hint={t("galleryEditor.hints.moreProjectsLabel", "Label above thumbnails")}
              />
            )}
          </div>
        </div>
      </Group>

      <Group
        title={t("galleryEditor.group.templateSettings", "Template Settings")}
        subtitle={t("galleryEditor.group.templateSettingsHelp", "Fields specific to the selected gallery template")}
        toggleLabel={t("galleryEditor.group.templateSettingsToggle", "Toggle")}
        defaultOpen={false}
      >
        <div className="space-y-6">
          {isGallery1(normalizedTemplate) && (
            <div className="space-y-4">
              <div className="text-sm font-semibold text-gray-900">
                {t("galleryEditor.gallery1.settings", "Gallery 1 Settings")}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field
                  label={t("galleryEditor.gallery1.cardButtonText", "Card Button Text")}
                  value={cfg?.cardButtonText ?? t("galleryEditor.gallery1.defaults.cardButtonText", "View Project Details")}
                  onChange={(v) => onChange("cardButtonText", v)}
                  hint={t("galleryEditor.gallery1.hints.cardButtonText", "Leave empty to hide the card button row")}
                />
                <Field
                  label={t("galleryEditor.gallery1.modalPrimaryButtonText", "Modal Primary Button Text")}
                  value={
                    cfg?.modalPrimaryButtonText ??
                    t("galleryEditor.gallery1.defaults.modalPrimaryButtonText", "View Live Project")
                  }
                  onChange={(v) => onChange("modalPrimaryButtonText", v)}
                  hint={t(
                    "galleryEditor.gallery1.hints.modalPrimaryButtonText",
                    "Leave empty to hide the primary button in the modal"
                  )}
                />
                <Field
                  label={t("galleryEditor.gallery1.modalOverviewTitle", "Modal Overview Title")}
                  value={
                    cfg?.modalOverviewTitle ?? t("galleryEditor.gallery1.defaults.modalOverviewTitle", "Project Overview")
                  }
                  onChange={(v) => onChange("modalOverviewTitle", v)}
                />
                <Field
                  label={t("galleryEditor.gallery1.modalFeaturesTitle", "Modal Features Title")}
                  value={
                    cfg?.modalFeaturesTitle ?? t("galleryEditor.gallery1.defaults.modalFeaturesTitle", "Key Features")
                  }
                  onChange={(v) => onChange("modalFeaturesTitle", v)}
                />
              </div>

              <details open className="rounded-xl border border-gray-200 bg-gray-50">
                <summary className="cursor-pointer select-none list-none px-4 py-3 flex items-center justify-between">
                  <div className="text-sm font-semibold text-gray-900">
                    {t("galleryEditor.gallery1.modalFeatures", "Modal Features")}
                  </div>
                  <div className="text-xs text-gray-400">
                    {t("galleryEditor.gallery1.modalFeaturesToggle", "Toggle")}
                  </div>
                </summary>

                <div className="px-4 pb-4 pt-1 space-y-3">
                  <div className="flex items-center justify-end">
                    <button
                      type="button"
                      onClick={addModalFeature}
                      className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium"
                    >
                      {t("galleryEditor.common.addFeature", "Add Feature")}
                    </button>
                  </div>

                  {modalFeatures.map((tt, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-3 items-center">
                      <div className="col-span-11">
                        <Field
                          label={`${t("galleryEditor.common.feature", "Feature")} ${idx + 1}`}
                          value={tt}
                          onChange={(v) => updateModalFeature(idx, v)}
                        />
                      </div>
                      <div className="col-span-1 flex justify-end">
                        <button
                          type="button"
                          className="text-red-600 text-sm font-medium"
                          onClick={() => removeModalFeature(idx)}
                        >
                          {t("galleryEditor.common.remove", "Remove")}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </details>

              <details open className="rounded-xl border border-gray-200 bg-gray-50">
                <summary className="cursor-pointer select-none list-none px-4 py-3 flex items-center justify-between">
                  <div className="text-sm font-semibold text-gray-900">
                    {t("galleryEditor.common.stats", "Stats")}
                  </div>
                  <div className="text-xs text-gray-400">
                    {t("galleryEditor.common.statsToggle", "Toggle")}
                  </div>
                </summary>

                <div className="px-4 pb-4 pt-1 space-y-3">
                  <div className="flex items-center justify-end">
                    <button
                      type="button"
                      onClick={addStat}
                      className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium"
                    >
                      {t("galleryEditor.common.addStat", "Add Stat")}
                    </button>
                  </div>

                  {stats.map((st, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-3 items-center">
                      <div className="col-span-5">
                        <Field
                          label={t("galleryEditor.common.value", "Value")}
                          value={st.value || ""}
                          onChange={(v) => updateStat(idx, { value: v })}
                        />
                      </div>
                      <div className="col-span-6">
                        <Field
                          label={t("galleryEditor.common.label", "Label")}
                          value={st.label || ""}
                          onChange={(v) => updateStat(idx, { label: v })}
                        />
                      </div>
                      <div className="col-span-1 flex justify-end">
                        <button
                          type="button"
                          className="text-red-600 text-sm font-medium"
                          onClick={() => removeStat(idx)}
                        >
                          {t("galleryEditor.common.remove", "Remove")}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </details>
            </div>
          )}

          {isGallery2(normalizedTemplate) && (
            <div className="space-y-3">
              <div className="text-sm font-semibold text-gray-900">
                {t("galleryEditor.gallery2.filterButtons", "Filter Buttons")}
              </div>

              {filterButtons.map((b, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-3 items-center">
                  <div className="col-span-5">
                    <Field
                      label={t("galleryEditor.gallery2.id", "Id")}
                      value={b.id || ""}
                      onChange={(v) => updateFilter(idx, { id: v })}
                    />
                  </div>
                  <div className="col-span-6">
                    <Field
                      label={t("galleryEditor.gallery2.label", "Label")}
                      value={b.label || ""}
                      onChange={(v) => updateFilter(idx, { label: v })}
                    />
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <button
                      type="button"
                      className="text-red-600 text-sm font-medium"
                      onClick={() => removeFilter(idx)}
                    >
                      {t("galleryEditor.common.remove", "Remove")}
                    </button>
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={addFilter}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium"
              >
                {t("galleryEditor.gallery2.addFilter", "Add Filter")}
              </button>
            </div>
          )}

          {isGallery3(normalizedTemplate) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field
                label={t("galleryEditor.gallery3.primaryButtonText", "Primary Button Text")}
                value={cfg?.primaryButtonText || ""}
                onChange={(v) => onChange("primaryButtonText", v)}
                hint={t(
                  "galleryEditor.gallery3.primaryButtonHint",
                  "Shown on the main project card if the project has a URL"
                )}
              />
            </div>
          )}
        </div>
      </Group>

      <Group
        title={t("galleryEditor.group.projects", "Projects")}
        subtitle={t("galleryEditor.group.projectsHelp", "Manage gallery items, images, and project details")}
        toggleLabel={t("galleryEditor.group.projectsToggle", "Toggle")}
        defaultOpen
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-gray-900">
              {t("galleryEditor.projects.title", "Projects")}
            </div>
            <button
              type="button"
              onClick={addProject}
              className="px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800"
            >
              {t("galleryEditor.projects.addProject", "Add Project")}
            </button>
          </div>

          <div className="space-y-4">
            {projects.map((p, projectIndex) => (
              <details key={p.id} open className="border border-gray-200 rounded-xl bg-white">
                <summary className="cursor-pointer select-none list-none px-4 py-3 flex items-center justify-between gap-3">
                  <div className="font-semibold text-gray-900">
                    {p.title || t("galleryEditor.projects.projectFallback", "Project")} {projectIndex + 1}
                  </div>

                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-400">
                      {t("galleryEditor.projects.projectToggle", "Toggle")}
                    </span>
                    <button
                      type="button"
                      className="text-red-600 text-sm font-medium"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        removeProject(p.id);
                      }}
                    >
                      {t("galleryEditor.common.remove", "Remove")}
                    </button>
                  </div>
                </summary>

                <div className="px-4 pb-4 pt-1 space-y-4">
                  <details open className="rounded-xl border border-gray-200 bg-gray-50">
                    <summary className="cursor-pointer select-none list-none px-4 py-3 flex items-center justify-between">
                      <div className="text-sm font-semibold text-gray-900">
                        {t("galleryEditor.group.projectBasics", "Project Basics")}
                      </div>
                      <div className="text-xs text-gray-400">
                        {t("galleryEditor.group.projectBasicsToggle", "Toggle")}
                      </div>
                    </summary>

                    <div className="px-4 pb-4 pt-1 space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field
                          label={t("galleryEditor.fields.title", "Title")}
                          value={p.title || ""}
                          onChange={(v) => updateProject(p.id, { title: v })}
                        />
                        <Field
                          label={t("galleryEditor.projects.category", "Category")}
                          value={p.category || ""}
                          onChange={(v) => updateProject(p.id, { category: v })}
                        />

                        <Field
                          label={t("galleryEditor.projects.description", "Description")}
                          type="textarea"
                          value={p.description || ""}
                          onChange={(v) => updateProject(p.id, { description: v })}
                        />

                        <Field
                          label={t("galleryEditor.projects.urlOptional", "URL (optional)")}
                          value={p.url || ""}
                          onChange={(v) => updateProject(p.id, { url: v })}
                        />
                      </div>
                    </div>
                  </details>

                  <details open className="rounded-xl border border-gray-200 bg-gray-50">
                    <summary className="cursor-pointer select-none list-none px-4 py-3 flex items-center justify-between">
                      <div className="text-sm font-semibold text-gray-900">
                        {t("galleryEditor.group.projectMedia", "Project Media")}
                      </div>
                      <div className="text-xs text-gray-400">
                        {t("galleryEditor.group.projectMediaToggle", "Toggle")}
                      </div>
                    </summary>

                    <div className="px-4 pb-4 pt-1 space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                      <Field
                        label={t("galleryEditor.projects.imageUrl", "Image URL")}
                        value={p.image || ""}
                        onChange={(v) => updateProject(p.id, { image: v })}
                      />

                      {uploadImage && (
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-gray-700">
                            {t("galleryEditor.projects.uploadImage", "Upload Image")}
                          </label>

                          <GalleryItemImageUploader
                            uploadImage={uploadImage}
                            currentImage={p.image || ""}
                            onUploaded={(url) => updateProject(p.id, { image: url })}
                            t={t}
                          />
                        </div>
                      )}
                    </div>

                      {p.image ? (
                        <img
                          src={p.image}
                          alt=""
                          className="w-full max-h-[260px] object-cover rounded-xl border border-gray-200 bg-white"
                        />
                      ) : (
                        <div className="w-full rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
                          {t("galleryEditor.projects.noImage", "No image selected yet")}
                        </div>
                      )}
                    </div>
                  </details>

                  {isGallery3(normalizedTemplate) && (
                    <details open className="rounded-xl border border-gray-200 bg-gray-50">
                      <summary className="cursor-pointer select-none list-none px-4 py-3 flex items-center justify-between">
                        <div className="text-sm font-semibold text-gray-900">
                          {t("galleryEditor.gallery3.results", "Results")}
                        </div>
                        <div className="text-xs text-gray-400">
                          {t("galleryEditor.gallery3.resultsToggle", "Toggle")}
                        </div>
                      </summary>

                      <div className="px-4 pb-4 pt-1 space-y-3">
                        <div className="flex items-center justify-end">
                          <button
                            type="button"
                            className="text-sm font-medium text-gray-700 hover:text-gray-900"
                            onClick={() => addResult(p.id)}
                          >
                            {t("galleryEditor.gallery3.addResult", "Add Result")}
                          </button>
                        </div>

                        {safeArr(p.results).map((r, idx) => (
                          <div key={idx} className="grid grid-cols-12 gap-3 items-center">
                            <div className="col-span-5">
                              <Field
                                label={t("galleryEditor.common.value", "Value")}
                                value={r.value || ""}
                                onChange={(v) => updateResult(p.id, idx, { value: v })}
                              />
                            </div>
                            <div className="col-span-6">
                              <Field
                                label={t("galleryEditor.common.label", "Label")}
                                value={r.label || ""}
                                onChange={(v) => updateResult(p.id, idx, { label: v })}
                              />
                            </div>
                            <div className="col-span-1 flex justify-end">
                              <button
                                type="button"
                                className="text-red-600 text-sm font-medium"
                                onClick={() => removeResult(p.id, idx)}
                              >
                                {t("galleryEditor.common.remove", "Remove")}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              </details>
            ))}

            {projects.length === 0 && (
              <div className="text-sm text-gray-500 border border-dashed border-gray-300 rounded-xl p-6 text-center">
                {t("galleryEditor.projects.empty", "No projects yet. Click “Add Project”.")}
              </div>
            )}
          </div>
        </div>
      </Group>
    </div>
  );
}